import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CustomerLoading } from '../../component/loading/loading';
import i18n from '../../i18n';
import apiManage from '../../request';
import { parseStaffContactQrResponse } from '../../utils/staff-contact-qr';
import { MenuStoreCommonDialog } from './menu-store-common-dialog.component';

type MenuStoreContactDialogProps = {
    /** 是否展示弹窗 */
    open: boolean;
    /** 关闭弹窗 */
    onClose: () => void;
    /** 菜单发布者 staff id（`created_by`），用于 `GET .../contact-qr` */
    creatorStaffId: string | undefined;
    /** 接口未返回制作人名称时，用于展示的兜底文案（如 `creator_contact.name`） */
    fallbackProducerName: string;
};

/**
 * 菜单商店「联系我们」弹窗：按发布者 id 拉取联系方式二维码（GET staff contact-qr）。
 */
export function MenuStoreContactDialog(props: MenuStoreContactDialogProps): React.ReactElement | null {
    const { open, onClose, creatorStaffId, fallbackProducerName } = props;
    /** 二维码图片地址 */
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    /** 接口返回的制作人名称（可选） */
    const [producerFromApi, setProducerFromApi] = useState<string | undefined>(undefined);
    /** 首次请求中 */
    const [loading, setLoading] = useState(false);
    /** 请求失败 */
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        setImageUrl(null);
        setProducerFromApi(undefined);
        setLoadError(false);
        if (!creatorStaffId?.trim()) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        void (async () => {
            try {
                const res = await apiManage.get_staff_id_contact_qr(creatorStaffId.trim());
                if (cancelled) {
                    return;
                }
                if (res && (res as { error?: boolean }).error) {
                    setLoadError(true);
                    setImageUrl(null);
                } else {
                    const parsed = parseStaffContactQrResponse(res);
                    setImageUrl(parsed?.imageUrl ?? null);
                    setProducerFromApi(parsed?.producerName);
                    setLoadError(false);
                }
            } catch {
                if (!cancelled) {
                    setLoadError(true);
                    setImageUrl(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, creatorStaffId]);

    if (!open) {
        return null;
    }

    /** 底部展示用制作人文案：接口优先，否则菜单详情兜底 */
    const producerDisplay = (producerFromApi?.trim() || fallbackProducerName?.trim() || i18n.t('menu_store_publisher_unknown')) as string;

    return (
        <MenuStoreCommonDialog
            title={i18n.t('menu_store_btn_contact')}
            onClose={onClose}
            dialogClassName="menu-store-contact-dialog"
            hideBottomClose
        >
            <Box className="menu-store-contact-dialog__content">
                {!creatorStaffId?.trim() ? (
                    <Typography variant="body1" className="menu-store-contact-dialog__hint">
                        {i18n.t('menu_store_contact_dialog_no_creator')}
                    </Typography>
                ) : loading ? (
                    <Box
                        className="menu-store-contact-dialog__loading-host"
                        role="status"
                        aria-live="polite"
                        aria-label={i18n.t('menu_store_contact_dialog_loading')}
                    >
                        <CustomerLoading />
                    </Box>
                ) : loadError ? (
                    <Typography variant="body1" className="menu-store-contact-dialog__hint">
                        {i18n.t('menu_store_contact_dialog_load_failed')}
                    </Typography>
                ) : imageUrl ? (
                    <>
                        <Box className="menu-store-contact-dialog__qr-wrap" aria-hidden>
                            <img className="menu-store-contact-dialog__qr-img" src={imageUrl} alt="" />
                        </Box>
                        <Typography variant="body1" component="p" className="menu-store-contact-dialog__producer">
                            {i18n.t('menu_store_contact_dialog_producer', { name: producerDisplay })}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body1" className="menu-store-contact-dialog__hint">
                        {i18n.t('menu_store_contact_dialog_no_qr')}
                    </Typography>
                )}
            </Box>
        </MenuStoreCommonDialog>
    );
}
